import { useTranslation } from 'react-i18next';

import { useEffect, useState } from 'react';

import { IconGift, IconRocket } from '@tabler/icons-react';

import { useNavigate } from '@tanstack/react-router';

import { Button, Modal, Stack, Text, ThemeIcon, Title } from '@optiro/ui';

import useApi from '@/hooks/useApi/useApi';
import { useAuth } from '@/providers/AuthProvider/AuthProvider';

function daysRemaining(trialEndsAt: string): number {
  const ms = new Date(trialEndsAt).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

export function TrialWelcomeModal() {
  const { userData, refetchUserData } = useAuth();
  const { t } = useTranslation('billing');
  const navigate = useNavigate();
  const { axiosInstance } = useApi();
  const [open, setOpen] = useState(false);
  const [extending, setExtending] = useState(false);

  useEffect(() => {
    if (!userData) return;
    if (userData.role !== 'administrator') return;

    const { trial_ends_at, status, trial_welcome_shown } = userData.company;

    if (trial_welcome_shown) return;
    if (status !== 'trial') return;
    if (!trial_ends_at || daysRemaining(trial_ends_at) <= 0) return;

    setOpen(true);
  }, [userData]);

  const dismiss = async () => {
    setOpen(false);
    await axiosInstance.post('/api/v1/company/dismiss_trial_welcome');
    await refetchUserData();
  };

  const handleGoToBilling = async () => {
    await dismiss();
    navigate({ to: '/app/billing' });
  };

  const handleExtendTrial = async () => {
    setExtending(true);
    try {
      await axiosInstance.post('/api/v1/company/extend_trial');
      await refetchUserData();
      setOpen(false);
    } catch {
      setOpen(false);
    } finally {
      setExtending(false);
    }
  };

  if (!userData?.company.trial_ends_at) return null;

  const days = daysRemaining(userData.company.trial_ends_at);

  return (
    <Modal opened={open} onClose={dismiss} centered withCloseButton={false} size="sm">
      <Stack align="center" gap="md" py="sm">
        <ThemeIcon size={56} radius="xl" variant="light" color="violet">
          <IconRocket size={28} />
        </ThemeIcon>

        <Stack gap={4} align="center">
          <Title order={3}>{t('trial.welcome.title')}</Title>
          <Text c="dimmed" size="sm" ta="center">
            {t('trial.welcome.description', { days })}
          </Text>
        </Stack>

        <Text size="sm" ta="center">
          {t('trial.welcome.body')}
        </Text>

        <Stack w="100%" gap="xs">
          {!userData.marketing_opt_in && (
            <Button
              fullWidth
              variant="light"
              color="teal"
              leftSection={<IconGift size={16} />}
              onClick={handleExtendTrial}
              loading={extending}
            >
              {t('trial.welcome.extend')}
            </Button>
          )}
          <Button fullWidth onClick={dismiss}>
            {t('trial.welcome.cta')}
          </Button>
          <Button fullWidth variant="subtle" color="gray" onClick={handleGoToBilling}>
            {t('trial.welcome.billing')}
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
}
